import React, { ChangeEvent, useEffect, useState } from 'react';
import { RootState } from 'redux/store';
import { setCraApiVariable } from 'redux/actions/CraApiVariableActions';
import FormicaLabel from 'components/custom/FormicaLabel';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from 'components/custom/FormicaSearchSelectBox';
import RoleHelper from 'RoleHelper';
import ComponentName from 'constant/ComponentName';
import LanguageUtil from 'util/LanguageUtil';
import { useDispatch, useSelector } from 'react-redux';
import { b64DecodeUnicode, b64EncodeUnicode } from 'util/Generator';
import Icon from 'icons/Icon';
import FormicaInput from 'components/custom/FormicaInput';
import CustomPasswordInput from 'components/custom/CustomPasswordInput';

export enum AuthType {
  NO_AUTH = 'No Auth',
  BASIC = 'Basic Auth',
  BEARER = 'Bearer Token',
}

export default function AuthorizationComponent({ name }: { name: string }) {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [base64, setBase64] = useState<string>('');
  const [bearerToken, setBearerToken] = useState<string>('');
  const { craApiVariables } = useSelector((state: RootState) => state.craApiVariable);
  const dispatch = useDispatch();
  const currentVariable = craApiVariables[name];

  useEffect(() => {
    if (!currentVariable?.headers) {
      return;
    }
    const token = currentVariable.headers['Authorization']?.value;
    if (token) {
      const tokenType = token.split(' ')[0];
      switch (tokenType) {
        case 'Bearer':
          currentVariable.auth = AuthType.BEARER;
          dispatch(setCraApiVariable(name, currentVariable));
          break;
        case 'Basic':
          currentVariable.auth = AuthType.BASIC;
          dispatch(setCraApiVariable(name, currentVariable));
          break;
        default:
          currentVariable.auth = AuthType.NO_AUTH;
          dispatch(setCraApiVariable(name, currentVariable));
      }
    }

    const base64 = currentVariable.headers['Authorization']?.value?.split('Basic ');
    if (base64 && base64.length > 1) {
      const decoded = b64DecodeUnicode(base64[1]);
      const [username, password] = decoded.split(':');
      setUsername(username);
      setPassword(password);
    }

    const bearerToken = currentVariable.headers['Authorization']?.value?.split('Bearer');
    if (bearerToken && bearerToken.length > 1) {
      setBearerToken(bearerToken[1].trim());
    }
  }, []);

  useEffect(() => {
    if (base64) {
      const prevDesc = currentVariable.headers['Authorization']?.description || '';
      currentVariable.headers['Authorization'] = { value: 'Basic ' + base64, description: prevDesc };
      dispatch(setCraApiVariable(name, currentVariable));
    }
  }, [base64]);

  const getAuthTypes = () => {
    return [
      {
        label: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.authorization.noAuth', defaultMessage: 'No Auth' }),
        value: AuthType.NO_AUTH,
      },
      {
        label: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.authorization.basicAuth', defaultMessage: 'Basic Auth' }),
        value: AuthType.BASIC,
      },
      {
        label: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.authorization.bearerToken', defaultMessage: 'Bearer Auth' }),
        value: AuthType.BEARER,
      },
    ];
  };

  const onUsernameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.currentTarget.value);
    const value = e.currentTarget.value + ':' + password;
    const encode = b64EncodeUnicode(value);
    setBase64(encode);
  };

  const onPasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.currentTarget.value);
    const value = username + ':' + e.currentTarget.value;
    const encode = b64EncodeUnicode(value);
    setBase64(encode);
  };

  const onChangeToken = (e: ChangeEvent<HTMLInputElement>) => {
    let token = e.target.value;
    if (token.startsWith('Bearer ')) {
      token = e.target.value.replace('Bearer ', '');
    } else token = '';
    const prevDesc = currentVariable.headers['Authorization']?.description || '';
    currentVariable.headers['Authorization'] = { value: 'Bearer ' + token, description: prevDesc };
    setBearerToken(token);
    dispatch(setCraApiVariable(name, currentVariable));
  };

  if (!currentVariable) {
    return null;
  }

  return (
    <div className={'flex flex-col w-full h-full mt-5'}>
      <div className={'flex flex-col gap-y-2 lg:w-[400px]'}>
        <FormicaLabel label={LanguageUtil.get({ id: 'common.label.type', defaultMessage: 'Type' })} />
        <FormicaSearchSelectBox
          disabled={!RoleHelper.canEdit('EnrichmentApi')}
          options={getAuthTypes()}
          onChange={(e: FormicaSelectBoxItem) => {
            currentVariable.auth = e.value as AuthType;
            dispatch(setCraApiVariable(name, currentVariable));
          }}
          rounded
          value={currentVariable.auth}
          placeholder={LanguageUtil.get({ id: 'common.label.select', defaultMessage: 'Select' })}
          data-testid={`${ComponentName.API_VARIABLE}_auth_type_select`}
        />
        {currentVariable.auth === AuthType.NO_AUTH && (
          <div className={'flex flex-col w-full h-full items-center justify-center'}>
            <Icon icon={'no-folder'} size={100} />
            <div className={'px-5 py-5 bg-f-gray mt-5 rounded shadow-sm'}>This request does not user any authorization.</div>
          </div>
        )}
        {currentVariable.auth === AuthType.BASIC && (
          <div className={'w-full h-full flex flex-col items-center justify-start gap-y-2'}>
            <div className={'flex flex-col w-full gap-y-2'}>
              <FormicaLabel label={'Username'} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('EnrichmentApi')}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onUsernameChange(e)}
                value={username}
                label={'Username'}
                data-testid={`${ComponentName.API_VARIABLE}_basic_auth_username_input`}
              />
            </div>
            <div className={'flex flex-col w-full gap-y-2 '}>
              <FormicaLabel label={'Password'} />
              <CustomPasswordInput
                disabled={!RoleHelper.canEdit('EnrichmentApi')}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onPasswordChange(e)}
                value={password}
                label={'Password'}
                data-testid={`${ComponentName.API_VARIABLE}_basic_auth_password_input`}
              />
            </div>
          </div>
        )}
        {currentVariable.auth === AuthType.BEARER && (
          <div className={'w-full h-full flex flex-col'}>
            <div className={'flex flex-col w-full gap-y-2'}>
              <FormicaLabel label={'Token'} />
              <FormicaInput
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeToken(e)}
                disabled={!RoleHelper.canEdit('EnrichmentApi')}
                value={'Bearer ' + bearerToken}
                label={'Token'}
                data-testid={`${ComponentName.API_VARIABLE}_bearer_auth_token_input`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
